import { Controller } from "stimulus"

// Filters the Contract <select> based on the currently selected Customer.
// The form ships with every contract rendered as an <option> carrying
// data-organization-id; this controller hides the options that don't
// match the picked customer, disables the select when none match, and
// resets the value if the prior contract is no longer in scope.

const HINT_PICK_CUSTOMER = "Pick a customer first."
const HINT_NO_CONTRACTS = "No contracts on this customer yet."
const HINT_ACTIVE = "Groups this subscription with other product lines on the same commercial agreement."

export default class extends Controller {
  static targets = ["organization", "contract", "hint", "automaticRenewal"]
  static values = { newRecord: Boolean }

  connect() {
    this.filter()
  }

  // Copy the picked contract's automatic_renewal flag onto the subscription
  // form as a default. Only fires on new subscription forms so editing a
  // sub never silently changes its auto-renewal when the user re-assigns
  // it to a different contract.
  applyRenewalDefault() {
    if (!this.newRecordValue) return
    if (!this.hasAutomaticRenewalTarget) return

    const selected = this.contractTarget.selectedOptions[0]
    if (!selected || selected.value === "") return

    this.automaticRenewalTarget.checked = selected.dataset.automaticRenewal === "true"
  }

  filter() {
    const orgId = this.organizationTarget.value
    const visibleCount = this.applyFilter(orgId)

    // The select stays enabled so the form always submits a contract_id
    // value (empty when nothing matches), which clears any stale contract
    // assignment that no longer belongs to the chosen customer. Disabling
    // the field would omit it from the PATCH and trap the old value.
    if (orgId === "") {
      this.setHint(HINT_PICK_CUSTOMER)
      this.contractTarget.value = ""
    } else if (visibleCount === 0) {
      this.setHint(HINT_NO_CONTRACTS)
      this.contractTarget.value = ""
    } else {
      this.setHint(HINT_ACTIVE)
      // If the previously-selected contract is no longer in scope, reset.
      const current = this.contractTarget.selectedOptions[0]
      if (current && current.value !== "" && current.hidden) {
        this.contractTarget.value = ""
      }
    }
  }

  applyFilter(orgId) {
    let visible = 0
    Array.from(this.contractTarget.options).forEach((opt) => {
      if (opt.value === "") return // keep the "— No contract" option
      const matches = opt.dataset.organizationId === orgId
      opt.hidden = !matches
      opt.disabled = !matches
      if (matches) visible += 1
    })
    return visible
  }

  setHint(hint) {
    if (this.hasHintTarget) this.hintTarget.textContent = hint
  }
}
